import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  type HeaderConfigDTO,
  type HeaderOrderUpsertDTO,
  type TimelineLayoutDTO,
  upsertTimelineLayoutDTOSchema,
} from '@tempo-io/roadmap-dto';
import { toast } from '@tempo-io/sui-sonner';
import type { z } from 'zod';
import { TimelineLayoutApi } from '@/lib/api/roadmapping/timeline-layout';
import { roadmappingQueryKeys } from '@/lib/query-keys/roadmapping';

type TimelineLayoutUpsertDTO = z.infer<typeof upsertTimelineLayoutDTOSchema>;

export const useUpsertTimelineLayoutMutation = (viewId: number, visualizationId: number) => {
  const queryClient = useQueryClient();
  const queryKey = roadmappingQueryKeys.timelineLayout.detail(viewId, visualizationId);

  return useMutation({
    mutationFn: async (partialLayout: Partial<TimelineLayoutUpsertDTO>) => {
      const existingLayout = queryClient.getQueryData<TimelineLayoutDTO>(queryKey);
      const mergedLayout = { ...existingLayout, ...partialLayout };
      const parsedLayout = upsertTimelineLayoutDTOSchema.parse(mergedLayout);
      return TimelineLayoutApi.upsertTimelineLayout(viewId, visualizationId, parsedLayout);
    },
    onMutate: (updatedLayout) => {
      const previousLayout = queryClient.getQueryData<TimelineLayoutDTO>(queryKey);

      // NEX-7049 - figure out what should be done when previousLayout is not found
      if (!previousLayout) {
        throw new Error('Previous layout not found in query cache');
      }

      const newLayout = { ...previousLayout, ...updatedLayout };
      queryClient.setQueryData(queryKey, newLayout);

      return { previousLayout };
    },
    onError: (err, _updatedLayout, context) => {
      toast.error(err.message);
      if (context?.previousLayout) {
        queryClient.setQueryData(queryKey, context.previousLayout);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
};

export const useUpdateTimelineHeaderOrderMutation = (viewId: number, visualizationId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      headerFieldId,
      headerOrder,
    }: {
      headerFieldId: string;
      headerOrder: HeaderOrderUpsertDTO;
    }) => {
      const updatedHeaderOrder = await TimelineLayoutApi.updateHeaderOrder(
        viewId,
        visualizationId,
        headerFieldId,
        headerOrder,
      );
      return { updatedHeaderOrder, headerFieldId, headerOrder };
    },
    onError: (err) => {
      toast.error(err.message);
    },
    onSuccess: ({ updatedHeaderOrder, headerFieldId, headerOrder }) => {
      queryClient.invalidateQueries({
        queryKey: roadmappingQueryKeys.timelineLayout.detail(viewId, visualizationId),
        refetchType: 'none',
      });
      queryClient.setQueriesData(
        {
          queryKey: roadmappingQueryKeys.timelineLayout.headerConfig({
            viewId,
            visualizationId,
            headerFieldId,
            parentFieldId: headerOrder.parentFieldHeaderId,
            parentHeaderId: headerOrder.parentHeaderId,
          }),
        },
        (prev: HeaderConfigDTO | undefined) => {
          if (!prev) return prev;
          return {
            ...prev,
            orderedHeaderIds: updatedHeaderOrder.orderedHeaderIds,
          };
        },
      );
    },
  });
};
